/**
 * Карточка сотрудника — всё о человеке в одном месте.
 *
 * Должность, вход в агенты по уровням, флаг денег (D47) и личные исключения
 * прав (D181). Каждое действие — готовый адрес платформы или `ops-api`, и
 * правила §8.4 проверяет сервер; скрытая кнопка здесь — удобство, а не защита.
 *
 * Табель, обучение и дисциплина пока показаны пометкой «скоро»: их экраны
 * переезжают на портал на этапах P4 и P5, и выдумывать цифры за них нельзя.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import { Notice } from '../../ui';
import { useMayEditTeam } from './access';
import {
  ACTION_NAMES,
  LEVEL_NAMES,
  SERVICE_NAMES,
  SERVICE_ORDER,
  SOURCE_NAMES,
  TEAM_KEYS,
  displayName,
  onCallNote,
  problemText,
  teamApi,
} from './api';
import type {
  MoneyPerson,
  PositionOption,
  StaffMember,
  RightObject,
  RightsScreen,
  TeamUser,
  UserRights,
  UserServiceRights,
} from './api';

type Result = { ok: boolean; text: string } | null;

export function EmployeeCard({
  user,
  me,
  positions,
}: {
  user: TeamUser;
  me: boolean;
  positions: PositionOption[];
}) {
  // Деньги — только владельцу, и не только кнопкой: сам адрес
  // `GET /api/ops/portal/money` закрыт «только владелец», и управляющему
  // блок показал бы отказ вместо содержания.
  const mayEdit = useMayEditTeam();
  const fired = user.employee_status === 'fired';
  return (
    <article className="team-card" aria-label={`Карточка: ${displayName(user)}`}>
      <header className="team-card__head">
        <h2 className="team-card__name">
          {displayName(user)}
          {me && <span className="team-faint"> (вы)</span>}
        </h2>
        <p className="team-card__meta">
          {user.is_host ? 'Владелец системы' : (user.position_name ?? 'Должность не назначена')}
          {fired && ' · уволен'}
        </p>
      </header>

      <PositionBlock user={user} positions={positions} />
      <AccessBlock user={user} />
      {!user.is_host && mayEdit && <MoneyBlock user={user} />}
      <CopyBlock user={user} positions={positions} />
      <RightsBlock user={user} />
      <SummaryBlock />
      {mayEdit && !user.is_host && !me && !fired && <FireBlock user={user} />}
    </article>
  );
}

/* ── Должность ───────────────────────────────────────────────────────── */

function PositionBlock({ user, positions }: { user: TeamUser; positions: PositionOption[] }) {
  const mayEdit = useMayEditTeam();
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const [confirmOwner, setConfirmOwner] = useState<number | null>(null);
  const mutation = useMutation<{ opened: string[] }, ApiError, number | null>({
    mutationFn: (positionId) => teamApi.setPosition(user.user_id, positionId),
    onSuccess: (data) => {
      setResult({
        ok: true,
        text: data.opened?.length
          ? `Должность сохранена. Открыт вход: ${data.opened.map((s) => SERVICE_NAMES[s] ?? s).join(', ')}.`
          : 'Должность сохранена.',
      });
      void client.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });

  // Должность, которой уже нет в справочнике, всё равно видна — молча
  // показать «не назначена» было бы неправдой.
  const known = positions.some((position) => position.id === user.position_id);
  const choose = (value: string) => {
    setResult(null);
    const chosen = value === '' ? null : Number(value);
    if (positions.find((position) => position.id === chosen)?.is_owner) {
      setConfirmOwner(chosen);
      return;
    }
    mutation.mutate(chosen);
  };

  return (
    <section className="team-block">
      <h3 className="block-title">Должность</h3>
      <p className="team-hint">
        По должности человек получает права в агентах. Вход в сам агент выдаётся отдельно — ниже.
      </p>
      <select
        className="select team-select"
        aria-label="Должность"
        value={user.position_id ?? ''}
        disabled={mutation.isPending || !mayEdit}
        onChange={(event) => choose(event.target.value)}
      >
        <option value="">должность не назначена</option>
        {!known && user.position_id !== null && (
          <option value={user.position_id}>
            {user.position_name ?? 'должность'} (убрана из справочника)
          </option>
        )}
        {positions.map((position) => (
          <option key={position.id} value={position.id}>
            {position.is_owner ? `${position.name} — все права` : position.name}
          </option>
        ))}
      </select>
      {confirmOwner !== null && (
        <div className="team-confirm" role="alert">
          <p>
            {displayName(user)} получит все права во всех агентах, включая финансы, и вход во все
            три агента. Назначить?
          </p>
          <div className="team-actions">
            <button
              type="button"
              className="button button--primary button--small"
              onClick={() => {
                mutation.mutate(confirmOwner);
                setConfirmOwner(null);
              }}
            >
              Назначить
            </button>
            <button
              type="button"
              className="button button--small"
              onClick={() => setConfirmOwner(null)}
            >
              Отмена
            </button>
          </div>
        </div>
      )}
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
    </section>
  );
}

/* ── Вход в агенты ───────────────────────────────────────────────────── */

function AccessBlock({ user }: { user: TeamUser }) {
  return (
    <section className="team-block">
      <h3 className="block-title">Вход в агенты</h3>
      <p className="team-hint">
        Выдаётся отдельно к каждому агенту: вход в обучение не открывает финансы. Управляющий
        получает все права агента, кроме денежных. Вход в финансы сразу открывает все их разделы —
        лишнее снимите ниже, в «Правах».
      </p>
      <ul className="team-rows">
        {SERVICE_ORDER.map((service) => (
          <AccessRow key={service} user={user} service={service} />
        ))}
      </ul>
    </section>
  );
}

function AccessRow({ user, service }: { user: TeamUser; service: string }) {
  const mayEdit = useMayEditTeam();
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const level = user.access[service] ?? '';
  const mutation = useMutation<unknown, ApiError, string>({
    mutationFn: (next) =>
      next === ''
        ? teamApi.revokeAccess(user.user_id, service)
        : teamApi.setAccess(user.user_id, service, next),
    onSuccess: (_data, next) => {
      setResult({ ok: true, text: next === '' ? 'Вход закрыт.' : 'Сохранено.' });
      void client.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });

  return (
    <li className="team-row">
      <span className="team-row__label">{SERVICE_NAMES[service]}</span>
      {user.is_host ? (
        <span className="pill">владелец — всё</span>
      ) : (
        <select
          className="select team-select team-select--small"
          aria-label={`Вход: ${SERVICE_NAMES[service]}`}
          value={level}
          disabled={mutation.isPending || !mayEdit}
          onChange={(event) => {
            setResult(null);
            mutation.mutate(event.target.value);
          }}
        >
          <option value="">нет входа</option>
          <option value="user">{LEVEL_NAMES.user}</option>
          <option value="admin">{LEVEL_NAMES.admin}</option>
        </select>
      )}
      {result && (
        <span className={`team-row__result${result.ok ? '' : ' team-row__result--error'}`}>
          {result.text}
        </span>
      )}
    </li>
  );
}

/* ── Копирование и скриншоты (D233) ──────────────────────────────────── */

function CopyBlock({ user, positions }: { user: TeamUser; positions: PositionOption[] }) {
  const mayEdit = useMayEditTeam();
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const mutation = useMutation<unknown, ApiError, boolean>({
    mutationFn: (on) => teamApi.setCopy(user.user_id, on),
    onSuccess: (_data, on) => {
      setResult({ ok: true, text: on ? 'Копирование разрешено.' : 'Копирование закрыто.' });
      void client.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });
  const owner = positions.find((item) => item.id === user.position_id)?.is_owner ?? false;
  const always = user.is_host ? 'владелец системы' : owner ? 'должность «Собственник»' : null;

  return (
    <section className="team-block">
      <h3 className="block-title">Копирование и скриншоты</h3>
      <p className="team-hint">
        Без разрешения сообщения ботов нельзя переслать, сохранить и скопировать, а на сайте —
        выделить, распечатать и выгрузить; поверх экрана стоит водяной знак с именем. Запретить
        скриншот сайта и фото экрана нельзя никому — знак показывает, чей это экран.
      </p>
      {always ? (
        <p className="team-row__label">Разрешено всегда: {always}.</p>
      ) : (
        <Switch
          label={user.copy_granted ? 'Может копировать' : 'Копировать нельзя'}
          checked={Boolean(user.copy_granted)}
          disabled={!mayEdit || mutation.isPending}
          onChange={(on) => {
            setResult(null);
            mutation.mutate(on);
          }}
        />
      )}
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
    </section>
  );
}

/* ── Увольнение ──────────────────────────────────────────────────────── */

/**
 * Одно действие вместо трёх (D231): статус «уволен», вход во все агенты
 * закрыт, все входы на сайт завершены. Раньше увольнения на экране не было
 * вовсе, а закрытый вход в агенты оставлял человека залогиненным на портале.
 */
function FireBlock({ user }: { user: TeamUser }) {
  const client = useQueryClient();
  const [asking, setAsking] = useState(false);
  const [result, setResult] = useState<Result>(null);
  const mutation = useMutation<{ revoked: string[]; sessions: number }, ApiError, void>({
    mutationFn: () => teamApi.fire(user.user_id),
    onSuccess: (data) => {
      const closed = data.revoked.map((s) => SERVICE_NAMES[s] ?? s);
      setResult({
        ok: true,
        text:
          `${displayName(user)} уволен. ` +
          (closed.length ? `Закрыт вход: ${closed.join(', ')}. ` : '') +
          (data.sessions ? 'Входы на сайт завершены.' : 'Открытых входов на сайт не было.'),
      });
      void client.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });

  return (
    <section className="team-block">
      <h3 className="block-title">Увольнение</h3>
      <p className="team-hint">
        Закрывает вход во все агенты и на сайт сразу, одним действием. История, заявки и права
        остаются в журнале.
      </p>
      {asking ? (
        <div className="team-confirm" role="alert">
          <p>
            Уволить {displayName(user)}? Боты перестанут отвечать ему сразу, сайт выкинет его на
            следующем же действии.
          </p>
          <div className="team-actions">
            <button
              type="button"
              className="button button--danger button--small"
              disabled={mutation.isPending}
              onClick={() => {
                setAsking(false);
                mutation.mutate();
              }}
            >
              Уволить
            </button>
            <button type="button" className="button button--small" onClick={() => setAsking(false)}>
              Отмена
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="button button--small"
          onClick={() => {
            setResult(null);
            setAsking(true);
          }}
        >
          Уволить…
        </button>
      )}
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
    </section>
  );
}

/* ── Доступ к деньгам ────────────────────────────────────────────────── */

function MoneyBlock({ user }: { user: TeamUser }) {
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const money = useQuery<{ people: MoneyPerson[] }, ApiError>({
    queryKey: TEAM_KEYS.money,
    queryFn: () => teamApi.money(),
  });
  const person = money.data?.people.find((item) => item.user_id === user.user_id) ?? null;
  const mutation = useMutation<{ message: string }, ApiError, boolean>({
    mutationFn: (on) => teamApi.setMoney(person!.employee_id, on),
    onSuccess: (data) => {
      setResult({ ok: true, text: data.message });
      void client.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });

  return (
    <section className="team-block">
      <h3 className="block-title">Доступ к деньгам</h3>
      <p className="team-hint">
        Накладные с суммами, оплаты и долги поставщикам. Выдаёте и снимаете только вы, поимённо;
        каждое изменение пишется в журнал.
      </p>
      {money.isPending ? (
        <Loading />
      ) : money.error ? (
        <Notice kind="error">{problemText(money.error)}</Notice>
      ) : person === null ? (
        <p className="team-faint">
          Сначала выдайте вход в операционный агент — деньги открываются внутри него.
        </p>
      ) : (
        <Switch
          label="Видит суммы и долги"
          checked={person.has_finance}
          disabled={mutation.isPending}
          onChange={(on) => {
            setResult(null);
            mutation.mutate(on);
          }}
        />
      )}
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
    </section>
  );
}

export function Switch({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      className={`team-switch${checked ? ' team-switch--on' : ''}`}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className="team-switch__track" aria-hidden="true">
        <span className="team-switch__thumb" />
      </span>
      <span className="team-switch__label">{label}</span>
    </button>
  );
}

/* ── Личные исключения прав ──────────────────────────────────────────── */

/** Право в клетке: есть ли оно, откуда и что сделает щелчок. */
export function cellState(view: UserServiceRights | undefined, key: string) {
  const source = view?.granted[key];
  const removed = view?.removed.includes(key) ?? false;
  const checked = source !== undefined;
  // Снять личное исключение — вернуть «как у должности»; поставить новое —
  // «добавить» или «убрать» относительно того, что даёт должность.
  const next: boolean | null = checked ? (source === 'added' ? null : false) : removed ? null : true;
  return { source, removed, checked, next, personal: source === 'added' || removed };
}

function RightsBlock({ user }: { user: TeamUser }) {
  const mayEdit = useMayEditTeam();
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const catalog = useQuery<RightsScreen, ApiError>({
    queryKey: TEAM_KEYS.rights,
    queryFn: () => teamApi.rights(),
  });
  const rights = useQuery<UserRights, ApiError>({
    queryKey: TEAM_KEYS.userRights(user.user_id),
    queryFn: () => teamApi.userRights(user.user_id),
  });
  const refresh = () => {
    void client.invalidateQueries({ queryKey: TEAM_KEYS.userRights(user.user_id) });
    void client.invalidateQueries({ queryKey: TEAM_KEYS.money });
  };
  const change = useMutation<
    unknown,
    ApiError,
    { service: string; object: string; action: string; granted: boolean | null }
  >({
    mutationFn: (input) => teamApi.setUserRight(user.user_id, input),
    onSuccess: () => {
      setResult({ ok: true, text: 'Право сохранено.' });
      refresh();
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });
  const reset = useMutation<unknown, ApiError, string>({
    mutationFn: (service) => teamApi.resetUserRights(user.user_id, service),
    onSuccess: () => {
      setResult({ ok: true, text: 'Права вернулись к правам должности.' });
      refresh();
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });
  const busy = change.isPending || reset.isPending || !mayEdit;

  return (
    <section className="team-block">
      <h3 className="block-title">Права</h3>
      <p className="team-hint">
        Галочка — право есть. Большинство приходит с должностью; щелчок меняет право только этому
        человеку, и оно помечается «лично».
      </p>
      {user.is_host && (
        <p className="team-faint">Владелец системы видит и может всё — менять здесь нечего.</p>
      )}
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
      {catalog.isPending || rights.isPending ? (
        <Loading text="Загружаю права…" />
      ) : catalog.error || rights.error ? (
        <ErrorState
          error={catalog.error ?? rights.error}
          onRetry={() => {
            void catalog.refetch();
            void rights.refetch();
          }}
        />
      ) : (
        SERVICE_ORDER.map((service) => {
          const objects = catalog.data.objects.filter((item) => item.service === service);
          const view = rights.data.services[service];
          if (objects.length === 0 || !view) return null;
          const noAccess = !user.is_host && !view.access_level;
          return (
            <div className="team-rights" key={service}>
              <div className="team-rights__head">
                <strong>{SERVICE_NAMES[service]}</strong>
                {!user.is_host && mayEdit && (
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    onClick={() => {
                      setResult(null);
                      reset.mutate(service);
                    }}
                  >
                    Как у должности
                  </button>
                )}
              </div>
              {noAccess && (
                <p className="team-faint">
                  Входа в этот агент нет — права начнут действовать, когда вы его выдадите.
                </p>
              )}
              <ul className="team-rights__list">
                {objects.map((item) => (
                  <RightRow
                    key={item.code}
                    item={item}
                    view={view}
                    disabled={busy || user.is_host}
                    onToggle={(action, granted) => {
                      setResult(null);
                      change.mutate({ service, object: item.code, action, granted });
                    }}
                  />
                ))}
              </ul>
            </div>
          );
        })
      )}
    </section>
  );
}

function RightRow({
  item,
  view,
  disabled,
  onToggle,
}: {
  item: RightObject;
  view: UserServiceRights;
  disabled: boolean;
  onToggle: (action: string, granted: boolean | null) => void;
}) {
  // «Оплаты» операционного агента — это флаг денег: его меняет переключатель
  // выше, через функцию сервиса с записью в журнал, а не голая галочка.
  const moneyFlag = item.service === 'ops' && item.code === 'payments';
  return (
    <li className="team-right">
      <span className="team-right__title" title={item.hint}>
        {item.title}
        {item.is_money && <span className="team-money">деньги</span>}
      </span>
      <span className="team-right__actions">
        {item.actions.map((action) => {
          const cell = cellState(view, `${item.code}:${action}`);
          const note = cell.checked
            ? (SOURCE_NAMES[cell.source ?? ''] ?? cell.source)
            : cell.removed
              ? 'лично снято'
              : '';
          return (
            <label key={action} className="team-check">
              <input
                type="checkbox"
                aria-label={`${item.title} — ${ACTION_NAMES[action] ?? action}`}
                checked={cell.checked}
                disabled={disabled || moneyFlag}
                onChange={() => onToggle(action, cell.next)}
              />
              {ACTION_NAMES[action] ?? action}
              {note && (
                <span className={cell.personal ? 'team-personal' : 'team-faint'}>{note}</span>
              )}
            </label>
          );
        })}
        {moneyFlag && <span className="team-faint">— переключателем «Доступ к деньгам»</span>}
      </span>
    </li>
  );
}

/* ── Сводка ──────────────────────────────────────────────────────────── */

function SummaryBlock() {
  const rows = [
    { title: 'Табель', about: 'приходы, уходы и часы', when: 'в разделе «Смена»' },
    { title: 'Дисциплина смены', about: 'чек-листы вовремя и с опозданием', when: 'в разделе «Смена»' },
    { title: 'Обучение', about: 'курсы, тесты и аттестация', when: 'в разделе «Обучение»' },
  ];
  return (
    <section className="team-block">
      <h3 className="block-title">Ещё о человеке</h3>
      <ul className="team-rows">
        {rows.map((row) => (
          <li className="team-row" key={row.title}>
            <span className="team-row__label">
              {row.title}
              <span className="team-faint"> — {row.about}</span>
            </span>
            <span className="pill" title={`Появится ${row.when}`}>
              скоро
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── Карточка сотрудника без Telegram ────────────────────────────────── */

/**
 * Человек из справочника, ещё ни разу не писавший ботам: штат заводят для
 * табеля раньше. Входа и прав у него нет — выдавать их некуда; когда он
 * напишет боту, заявка привяжется к этой же записи (D234), и карточка
 * станет полной.
 */
export function StaffCard({
  member,
  positions,
}: {
  member: StaffMember;
  positions: PositionOption[];
}) {
  const mayEdit = useMayEditTeam();
  const client = useQueryClient();
  const [result, setResult] = useState<Result>(null);
  const [asking, setAsking] = useState(false);
  const position = useMutation<unknown, ApiError, number | null>({
    mutationFn: (positionId) => teamApi.setStaffPosition(member.employee_id, positionId),
    onSuccess: () => {
      setResult({ ok: true, text: 'Должность сохранена.' });
      void client.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });
  const fire = useMutation<unknown, ApiError, void>({
    mutationFn: () => teamApi.fireStaff(member.employee_id),
    onSuccess: () => {
      setResult({ ok: true, text: `${member.full_name} уволен.` });
      void client.invalidateQueries({ queryKey: ['team'] });
    },
    onError: (error) => setResult({ ok: false, text: problemText(error) }),
  });

  return (
    <article className="team-card" aria-label={`Карточка: ${member.full_name}`}>
      <header className="team-card__head">
        <h2 className="team-card__name">{member.full_name}</h2>
        <p className="team-card__meta">
          {member.position_name ?? 'Должность не назначена'} · нет Telegram
        </p>
        {member.on_call && <p className="team-card__meta">{onCallNote(member)}</p>}
      </header>

      <section className="team-block">
        <h3 className="block-title">Должность</h3>
        <select
          className="select team-select"
          aria-label="Должность"
          value={member.position_id ?? ''}
          disabled={!mayEdit || position.isPending}
          onChange={(event) => {
            setResult(null);
            position.mutate(event.target.value === '' ? null : Number(event.target.value));
          }}
        >
          <option value="">должность не назначена</option>
          {positions
            .filter((item) => !item.is_owner)
            .map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
        </select>
      </section>

      <section className="team-block">
        <h3 className="block-title">Вход в агенты</h3>
        <p className="team-hint">
          Выдать нечего: человек ещё не писал ни одному боту. Попросите его открыть любой бот
          ресторана и подать заявку — в окне заявки выберите его из справочника, и вход, права и
          табель окажутся у одного и того же человека.
        </p>
      </section>

      {mayEdit && (
        <section className="team-block">
          <h3 className="block-title">Увольнение</h3>
          {asking ? (
            <div className="team-confirm" role="alert">
              <p>Уволить {member.full_name}? Он пропадёт из списков и табеля.</p>
              <div className="team-actions">
                <button
                  type="button"
                  className="button button--danger button--small"
                  disabled={fire.isPending}
                  onClick={() => {
                    setAsking(false);
                    fire.mutate();
                  }}
                >
                  Уволить
                </button>
                <button type="button" className="button button--small" onClick={() => setAsking(false)}>
                  Отмена
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="button button--small" onClick={() => setAsking(true)}>
              Уволить…
            </button>
          )}
        </section>
      )}
      {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}
    </article>
  );
}
